import { LoadScreen } from "@/core/components/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { useWalletBalance } from "@/features/wallet/api/use-wallet-balance";
import { AmountMinor } from "@/features/shared/components/amount-minor";

/**
 * Carte des soldes d'un portefeuille (`GET /Wallets/{id}/balance`) — montants
 * `*Minor` en unité mineure (§4.3), formatés via `AmountMinor` (langue
 * courante + `NoTranslate`, règle notranslate sur les données financières).
 */
export function WalletBalanceCard({
  walletId,
  currency,
}: {
  walletId: string;
  /** Devise du portefeuille — repli sur la devise du solde, puis GNF. */
  currency?: string;
}) {
  const { t } = useTypedTranslation();
  // Les clés wallet.wallets.* vivent dans features/wallet/i18n/wallets.json
  // en attendant leur fusion dans src/locales par le coordinateur.
  const tWallet = (key: string) => t(key as TFunctionType);
  const { data: balance, isLoading } = useWalletBalance(walletId);

  const effectiveCurrency = balance?.currency ?? currency;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {tWallet("wallet.wallets.balance.title")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex h-24 items-center justify-center">
            <LoadScreen />
          </div>
        ) : (
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <dt className="text-sm text-muted-foreground">
                {tWallet("wallet.wallets.balance.available")}
              </dt>
              <dd className="text-xl font-semibold">
                <AmountMinor
                  amountMinor={balance?.availableBalanceMinor}
                  currency={effectiveCurrency}
                />
              </dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground">
                {tWallet("wallet.wallets.balance.pending")}
              </dt>
              <dd className="text-xl font-semibold">
                <AmountMinor
                  amountMinor={balance?.pendingBalanceMinor}
                  currency={effectiveCurrency}
                />
              </dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground">
                {tWallet("wallet.wallets.balance.ledger")}
              </dt>
              <dd className="text-xl font-semibold">
                <AmountMinor
                  amountMinor={balance?.ledgerBalanceMinor}
                  currency={effectiveCurrency}
                />
              </dd>
            </div>
          </dl>
        )}
      </CardContent>
    </Card>
  );
}
